<template>
    <div class="chatbot-selector">
        <Modal2
            v-if="selectedStory"
            @cancel="selectedStory = null"
            :showTitle="true"
            :title="$t('message.chatbotSettings')"
            :showFooter="false"
            large
        >
            <ForwardChatbotTunings
                :default-story-id="selectedStory._id"
                @cancel="selectedStory = null"
                @send="handleForward"
            />
        </Modal2>

        <div class="empty" v-if="stories.length === 0">
            <div class="title">{{ $st("mesage.chatbotScenarioNotFound") }}</div>
            <div class="message">{{ $t("message.noChatbotScenario") }}</div>
        </div>
        <template v-else>
            <div class="items">
                <div v-if="stories.length <= 0">{{ $t("message.scenarioNotFound") }}</div>

                <div
                    class="item-card"
                    v-for="(item, index) of stories"
                    :data-id="item._id"
                    :data-pointer="index"
                    :key="item._id"
                    @click="selectStory(item)"
                    :class="{
                        selected: pointerIndex === index,
                        hoverSelected: pointerIndex === index,
                    }"
                >
                    <div class="title-wrapper">
                        <div class="title">
                            {{ item.title }}
                        </div>
                    </div>
                    <div class="message-preview">
                        {{ item.description || $t("message.descNotAvailable") }}
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import Vue from "vue";
import { mapActions, mapState } from "vuex";
import log2 from "../../../../../../utils/log2";
import Modal2 from "../../../../../common/Modal2.vue";
import ForwardChatbotTunings from "./ForwardChatbotTunings.vue";

const log = log2("ChatbotSelector");

export default Vue.extend({
    name: "ChatbotSelector",
    components: { Modal2, ForwardChatbotTunings },
    data() {
        return {
            pointerIndex: 0,
            cancels: [],
            selectedStory: null,
        };
    },
    computed: {
        ...mapState({
            stories: (state) => state.CB.stories,
        }),
    },
    async mounted() {
        await this.listStories();

        const keydownCallback = (e) => {
            if (e.key === "ArrowDown") {
                const index = Math.min(this.pointerIndex + 1, this.stories.length - 1);
                const id = this.stories.find((item, i) => i === index)?._id;
                const el = document.querySelector(`div[data-id='${id}']`);
                el?.scrollIntoView({
                    behavior: "smooth",
                });
                this.pointerIndex = index;
            } else if (e.key === "ArrowUp") {
                const index = Math.max(this.pointerIndex - 1, 0);
                const id = this.stories.find((item, i) => i === index)?._id;
                const el = document.querySelector(`div[data-id='${id}']`);
                el?.scrollIntoView({
                    behavior: "smooth",
                });
                this.pointerIndex = index;
            } else if (e.key === "Enter") {
                const item = this.stories.find((item, i) => i === this.pointerIndex);
                this.selectStory(item);
            }
        };
        window.addEventListener("keydown", keydownCallback);
        this.cancels.push(() => window.removeEventListener("keydown", keydownCallback));
    },
    methods: {
        ...mapActions({
            listStories: "CB/listStories",
        }),
        selectStory(story) {
            this.selectedStory = story;
        },
        handleForward(config) {
            this.$emit("send", {
                ...config,
            });
            this.selectedStory = null;
        },
    },
    beforeDestroy() {
        this.cancels.forEach((cancel) => cancel());
    },
});
</script>

<style scoped lang="scss">
.chatbot-selector {
    width: 100%;
    display: flex;
    height: 100%;
    min-height: inherit;

    .empty {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 10px;
        height: 100%;
        width: 100%;

        .title {
            font-size: 16px;
            font-weight: 700;
            color: #6c757d;
        }

        .message {
            font-size: 14px;
            font-weight: 500;
            color: #6c757d;
        }
    }

    .items {
        display: flex;
        flex-direction: column;
        width: 100%;
        background-color: #fff;
        overflow: auto;

        .item-card {
            display: flex;
            flex-direction: column;
            gap: 3px;
            padding: 5px 5px;
            cursor: pointer;
            border-bottom: 1px solid #f1f4f7;

            &:hover,
            &.selected {
                background-color: #f5f5f5;
            }

            &.hoverSelected {
                box-shadow: 2px 2px 2px 2px var(--color-bg-primary-hover);
            }

            .title-wrapper {
                display: flex;
                justify-content: space-between;

                .title {
                    font-size: 14px;
                    font-weight: 700;
                    color: #6c757d;
                    display: flex;
                    align-items: center;
                }
            }

            .message-preview {
                font-size: 12px;
                font-weight: 500;
                color: #6c757d;
                -webkit-line-clamp: 2;
                overflow: hidden;
                display: -webkit-box;
                -webkit-box-orient: vertical;
                border-left: 3px solid #6c757d;
                padding-left: 3px;
            }
        }
    }
}
</style>
